import { FC } from "react";
import { useSnackbar, VariantType } from "notistack";

import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import { createAssetItem } from "@/api/lab-assets";
import type { CreateLabAssetBody, LabAssetType } from "../labAssets.types";
import DefaultLabAssetForm, { LabAssetPayload } from "./DefaultLabAssetForm";

interface CreateLabAssetFormProps {
  // Chosen in the create modal's type step; POST /assets requires it.
  assetType: LabAssetType;
  width?: number | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
  // Returns to the modal's type step; rendered in the form footer.
  onBack?: () => void;
}

// Software rows keep their display name in license_name and leave name null
// (see CreateLabAssetBody in lab-assets.types.ts) — the /assets/names lookup
// for software filters on license_name, so sending name alone would hide the
// row there.
const toCreateBody = (
  data: LabAssetPayload,
  assetType: LabAssetType
): CreateLabAssetBody => {
  if (assetType === "software") {
    return {
      ...data,
      asset_type: assetType,
      name: null,
      license_name: data.name,
    };
  }
  return { ...data, asset_type: assetType };
};

const GENERIC_CREATE_ERROR =
  "An error occurred while creating this asset. Please try again.";

const CreateLabAssetForm: FC<CreateLabAssetFormProps> = ({
  assetType,
  width,
  onSubmit,
  onCancel,
  onSubmittingChange,
  onBack,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (data: LabAssetPayload) => {
    try {
      // skipErrorToast: the axios interceptor would toast the raw status
      // text too, stacking two error snackbars for one failed request.
      const result = await createAssetItem(toCreateBody(data, assetType), {
        skipErrorToast: true,
      });
      // The API layer resolves (never rejects) on HTTP errors and returns
      // { success: false, error } instead — treat that as a failure so the
      // modal stays open with the user's values intact. Surface the server's
      // reason when it sends one: a 403 (tenant missing asset:* permission
      // rows) and a 400 (server-side validation) aren't fixable by retrying.
      if (!result || result.success === false) {
        throw new Error(
          result?.error?.response?.data?.message || GENERIC_CREATE_ERROR
        );
      }

      // The API writes the activity-log entry for the create; logging here
      // too would duplicate it into the admin feed.
      onSubmit?.();
      enqueueSnackbar(`Asset ${data.name} created`, {
        variant: "success" as VariantType,
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        error instanceof Error && error.message
          ? error.message
          : GENERIC_CREATE_ERROR,
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <DefaultLabAssetForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        onSubmittingChange={onSubmittingChange}
        onBack={onBack}
        buttonProps={{ submitText: "Add Asset" }}
      />
    </FormWrapper>
  );
};

export default CreateLabAssetForm;
